import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { getUploadBase } from '../utils/api';
import '../css/ProfileSettings.css';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      _id
      name
      email
      telephone
      avatar
      bio
      privacy {
        showEmail
        showTelephone
        allowDirectMessages
        allowGroupInvites
      }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($name: String, $email: String, $telephone: String, $avatar: String, $bio: String) {
    updateUser(name: $name, email: $email, telephone: $telephone, avatar: $avatar, bio: $bio) {
      _id
      name
      email
      telephone
      avatar
      bio
    }
  }
`;

const DELETE_AVATAR_MUTATION = gql`
  mutation DeleteAvatar {
    deleteAvatar
  }
`;

const DELETE_MY_ACCOUNT = gql`
    mutation DeleteMyAccount {
        deleteMyAccount
    }
`;

const UPDATE_PRIVACY_SETTINGS = gql`
  mutation UpdatePrivacySettings(
    $showEmail: Boolean!
    $showTelephone: Boolean!
    $allowDirectMessages: Boolean!
    $allowGroupInvites: Boolean!
  ) {
    updatePrivacySettings(
      showEmail: $showEmail
      showTelephone: $showTelephone
      allowDirectMessages: $allowDirectMessages
      allowGroupInvites: $allowGroupInvites
    ) {
      _id
      privacy {
        showEmail
        showTelephone
        allowDirectMessages
        allowGroupInvites
      }
    }
  }
`;

const ProfileSettings = () => {
    const [profile, setProfile] = useState({
        _id: '',
        name: '',
        email: '',
        avatar: '',           // URL of avatar from server
        telephone: '',
        bio: '',
        notifications: true,
        theme: 'light',
        privacy: {
            showEmail: false,
            showTelephone: false,
            allowDirectMessages: true,
            allowGroupInvites: true
        },
    });

    const [avatarPreview, setAvatarPreview] = useState('');  // Base64 or URL for preview
    const [uploading, setUploading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    // Apollo client and queries/mutations
    const client = useApolloClient();
    const { loading, error: queryError, data } = useQuery(GET_CURRENT_USER, {
        fetchPolicy: 'cache-and-network'
    });

    // Update mutation with explicit cache update
    const [updateUser] = useMutation(UPDATE_USER_MUTATION, {
        update: (cache, { data: { updateUser: updatedUser } }) => {
            // Explicitly update the cache with the current user's data
            cache.writeQuery({
                query: GET_CURRENT_USER,
                data: {
                    me: updatedUser
                }
            });
        }
    });

    // Delete avatar mutation with explicit cache update
    const [deleteAvatar] = useMutation(DELETE_AVATAR_MUTATION, {
        update: (cache) => {
            // Explicitly update the cache to remove avatar
            const data = cache.readQuery({ query: GET_CURRENT_USER });
            if (data && data.me) {
                cache.writeQuery({
                    query: GET_CURRENT_USER,
                    data: {
                        me: {
                            ...data.me,
                            avatar: null
                        }
                    }
                });
            }
        }
    });
    const [deleteMyAccount] = useMutation(DELETE_MY_ACCOUNT);
    const [updatePrivacySettings] = useMutation(UPDATE_PRIVACY_SETTINGS);

    // Load current user profile on mount
    useEffect(() => {
        if (data && data.me) {
            const user = data.me;
            setProfile({
                _id: user._id,
                name: user.name,
                email: user.email,
                avatar: user.avatar || '',
                telephone: user.telephone || '',
                bio: user.bio || '',
                notifications: true,
                theme: 'light',
                privacy: {
                    showEmail: user.privacy?.showEmail ?? false,
                    showTelephone: user.privacy?.showTelephone ?? false,
                    allowDirectMessages: user.privacy?.allowDirectMessages ?? true,
                    allowGroupInvites: user.privacy?.allowGroupInvites ?? true
                },
            });
            if (user.avatar) {
                setAvatarPreview(user.avatar);
            }
        }
    }, [data]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProfile(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handlePrivacyChange = (e) => {
        const { name, checked } = e.target;
        setProfile(prev => ({
            ...prev,
            privacy: { ...prev.privacy, [name]: checked }
        }));
    };

    // Handle avatar file selection and upload
    const handleAvatarChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size exceeds 5MB limit');
            return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onload = (ev) => {
            setAvatarPreview(ev.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to server
        await uploadAvatar(file);
    };

    const uploadAvatar = async (file) => {
        setUploading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const token = localStorage.getItem('token') || '';
            const response = await fetch(`${getUploadBase()}/uploadavatar`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                },
                body: formData
            });

            const data = await response.json();
            
            if (response.ok && data.url) {
                // Update user in database with new avatar URL using Apollo mutation
                // The mutation's update function will handle cache updates
                await updateUser({
                    variables: {
                        avatar: data.url
                    }
                });
                setAvatarPreview(data.url);
                setSaved(true);
                setTimeout(() => setSaved(false), 3000);
            } else {
                setError(data.error || 'Failed to upload avatar');
            }
        } catch (err) {
            setError('Failed to upload avatar');
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAvatar = async () => {
        if (!profile.avatar) return;
        
        if (!window.confirm('Are you sure you want to delete your avatar?')) {
            return;
        }

        try {
            await deleteAvatar();
            setProfile(prev => ({
                ...prev,
                avatar: ''
            }));
            setAvatarPreview('');
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError('Failed to delete avatar');
            console.error('Delete error:', err);
        }
    };

    const handleReloadAvatar = () => {
        // Reload avatar by refetching the current user data through Apollo
        client.refetchQueries({
            include: [GET_CURRENT_USER]
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Only update fields that are not empty
            const updateVars = {};
            if (profile.name) updateVars.name = profile.name;
            if (profile.email) updateVars.email = profile.email;
            if (profile.telephone) updateVars.telephone = profile.telephone;
            if (profile.bio !== undefined) updateVars.bio = profile.bio;

            if (Object.keys(updateVars).length > 0) {
                await updateUser({
                    variables: updateVars
                });
            }
            await updatePrivacySettings({ variables: profile.privacy });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError('Failed to save profile');
            console.error('Update profile error:', err);
        }
    };

    const handleDeleteAccount = async () => {
        const confirmed = window.confirm('Удалить свой аккаунт? Записи, сообщения и загруженные файлы сохранятся, профиль станет анонимным. Это действие нельзя отменить.');
        if (!confirmed) return;
        try {
            await deleteMyAccount();
            localStorage.removeItem('token');
            await client.clearStore();
            window.location.assign('/login');
        } catch (err) {
            setError('Не удалось удалить аккаунт. Попробуйте позже.');
            console.error('Delete account error:', err);
        }
    };

    if (loading) {
        return <div className="profile-settings"><p>Loading profile...</p></div>;
    }

    if (queryError) {
        return <div className="profile-settings"><p>Error loading profile: {queryError.message}</p></div>;
    }

    return (
        <div className="profile-settings">
            <h2>Profile Settings</h2>
            <form onSubmit={handleSubmit}>
                <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={profile.name}
                    onChange={handleChange}
                />
                <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={profile.email}
                    onChange={handleChange}
                />
                <input
                    type="tel"
                    name="telephone"
                    placeholder="Phone"
                    value={profile.telephone}
                    onChange={handleChange}
                />

                {/* avatar file input and preview */}
                <div className="avatar-group">
                    {avatarPreview && (
                        <div className="avatar-preview-container">
                            <img
                                src={avatarPreview}
                                alt="Avatar preview"
                                className="avatar-preview"
                            />
                        </div>
                    )}
                    {!avatarPreview && (
                        <div className="avatar-placeholder">No avatar</div>
                    )}
                    <div className="avatar-controls">
                        <label className="avatar-upload-btn">
                            <input
                                type="file"
                                name="avatar"
                                accept="image/*"
                                onChange={handleAvatarChange}
                                disabled={uploading}
                                style={{ display: 'none' }}
                            />
                            {uploading ? 'Uploading...' : 'Upload Avatar'}
                        </label>
                        {profile.avatar && (
                            <>
                                <button 
                                    type="button"
                                    className="avatar-reload-btn"
                                    onClick={handleReloadAvatar}
                                    disabled={uploading}
                                >
                                    Reload Avatar
                                </button>
                                <button 
                                    type="button"
                                    className="avatar-delete-btn"
                                    onClick={handleDeleteAvatar}
                                    disabled={uploading}
                                >
                                    Delete Avatar
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <textarea
                    name="bio"
                    placeholder="Bio"
                    value={profile.bio}
                    onChange={handleChange}
                />
                <label>
                    <input
                        type="checkbox"
                        name="notifications"
                        checked={profile.notifications}
                        onChange={handleChange}
                    />
                    Enable Notifications
                </label>
                <select name="theme" value={profile.theme} onChange={handleChange}>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                </select>
                <fieldset>
                    <legend>Конфиденциальность</legend>
                    <label>
                        <input type="checkbox" name="showEmail" checked={profile.privacy.showEmail} onChange={handlePrivacyChange} />
                        Показывать email в профиле
                    </label>
                    <label>
                        <input type="checkbox" name="showTelephone" checked={profile.privacy.showTelephone} onChange={handlePrivacyChange} />
                        Показывать телефон в профиле
                    </label>
                    <label>
                        <input type="checkbox" name="allowDirectMessages" checked={profile.privacy.allowDirectMessages} onChange={handlePrivacyChange} />
                        Принимать личные сообщения не только от друзей
                    </label>
                    <label>
                        <input type="checkbox" name="allowGroupInvites" checked={profile.privacy.allowGroupInvites} onChange={handlePrivacyChange} />
                        Принимать приглашения в групповые чаты
                    </label>
                </fieldset>
                <p>Текст чатов шифруется в базе данных, но сервер расшифровывает его для доставки; вложения пока не шифруются на диске. Сквозного шифрования нет. Защита при передаче требует HTTPS на сервере.</p>
                <button type="submit">Save Settings</button>
            </form>
                <section className="profile-delete-account">
                    <h3>Удаление аккаунта</h3>
                    <p>Ваши публикации и переписки сохранятся, а данные профиля будут обезличены.</p>
                    <button type="button" className="avatar-delete-btn" onClick={handleDeleteAccount}>Удалить мой аккаунт без контента</button>
                </section>
            {error && <p className="error">{error}</p>}
            {saved && <p className="success">Settings saved successfully!</p>}
        </div>
    );
};

export default ProfileSettings;